<template>
  <div class="py-4">
    <div class="d-flex align-center mb-4">
      <div>
        <div class="text-h6 font-weight-bold">Centro de carga</div>
        <div class="caption grey--text">
          Cargá una lista del proveedor, un Excel o productos a mano. Cada fila se
          resuelve contra el catálogo y se aplica cuando está lista.
        </div>
      </div>
      <v-spacer />
      <v-btn depressed color="primary" class="text-none" @click="abrirNueva">
        <v-icon left>mdi-plus</v-icon> Nueva carga
      </v-btn>
    </div>

    <v-progress-linear v-if="loading" indeterminate />

    <v-row>
      <v-col v-for="c in cargas" :key="c.id" cols="12" md="6" lg="4">
        <v-card outlined class="pa-4 carga" @click="abrir(c)">
          <div class="d-flex align-center">
            <div class="font-weight-bold">{{ c.nombre }}</div>
            <v-spacer />
            <v-btn icon small @click.stop="descartar(c)"><v-icon small>mdi-delete-outline</v-icon></v-btn>
          </div>
          <div class="caption grey--text mb-3">
            {{ origenes[c.origen] }} · {{ c.creado_por_nombre || "—" }} · {{ fecha(c.creado) }}
          </div>
          <div class="d-flex flex-wrap" style="gap: 6px">
            <v-chip x-small color="green" outlined>{{ c.conteos.seguras }} seguras</v-chip>
            <v-chip x-small color="orange" outlined>{{ c.conteos.dudosas }} dudosas</v-chip>
            <v-chip x-small color="blue" outlined>{{ c.conteos.nuevas }} nuevas</v-chip>
            <v-chip x-small outlined>{{ c.conteos.aplicadas }} / {{ c.conteos.total }} aplicadas</v-chip>
          </div>
        </v-card>
      </v-col>
      <v-col v-if="!cargas.length && !loading" cols="12">
        <div class="caption grey--text">No hay cargas en curso.</div>
      </v-col>
    </v-row>

    <v-dialog v-model="dialogNueva" max-width="520">
      <v-card>
        <v-card-title>Nueva carga</v-card-title>
        <v-card-text>
          <v-btn-toggle v-model="nueva.tipo" mandatory class="mb-4" color="primary">
            <v-btn value="LISTA" small class="text-none">Lista del proveedor</v-btn>
            <v-btn value="EXCEL" small class="text-none">Excel</v-btn>
            <v-btn value="MANUAL" small class="text-none">A mano</v-btn>
            <v-btn v-if="puedeUsarAsistente" value="FACTURA" small class="text-none">Factura</v-btn>
          </v-btn-toggle>

          <v-file-input
            v-if="nueva.tipo === 'FACTURA'"
            v-model="nueva.archivo"
            accept="application/pdf,image/*"
            label="Factura (PDF o foto)"
            hint="Se lee con el asistente: proveedor, renglones, cantidades y precios"
            persistent-hint
            outlined
            dense
            class="mb-3"
          />

          <v-autocomplete
            v-if="nueva.tipo !== 'FACTURA'"
            v-model="nueva.proveedor"
            :items="proveedores"
            :search-input.sync="buscarProveedor"
            item-text="nombre"
            item-value="id"
            label="Proveedor"
            :hint="nueva.tipo === 'LISTA' ? '' : 'Opcional: con proveedor, lo confirmado queda aprendido en su mapa'"
            persistent-hint
            clearable
            outlined
            dense
            no-filter
            @change="fetchListas"
          />
          <v-select
            v-if="nueva.tipo === 'LISTA'"
            v-model="nueva.lista"
            :items="listas"
            item-value="id"
            :item-text="(l) => `${l.nombre} · ${l.items} productos${l.fecha ? ' · ' + l.fecha : ''}`"
            label="Lista"
            :no-data-text="nueva.proveedor ? 'Este proveedor no tiene listas cargadas' : 'Elegí el proveedor'"
            outlined
            dense
            class="mt-3"
          />
          <v-file-input
            v-if="nueva.tipo === 'EXCEL'"
            v-model="nueva.archivo"
            accept=".xlsx"
            label="Excel (.xlsx)"
            hint="Columnas: Código, Descripción, Código de barras, Costo, IVA"
            persistent-hint
            outlined
            dense
            class="mt-3"
          />
          <v-text-field v-model="nueva.nombre" label="Nombre (opcional)" outlined dense class="mt-3" />
          <v-select
            v-model="nueva.sucursales"
            :items="sucursales"
            item-text="nombre"
            item-value="id"
            label="Sucursales"
            hint="Dónde se habilitan los nuevos y se actualiza el costo. Vacío: todas."
            persistent-hint
            multiple
            outlined
            dense
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text class="text-none" @click="dialogNueva = false">Cancelar</v-btn>
          <v-btn depressed color="primary" class="text-none" :loading="creando" :disabled="!puedeCrear" @click="crear">
            Empezar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { getApiErrorMessage } from "@/util/apiError";

export default {
  name: "ProductosCentroCarga",
  data() {
    return {
      cargas: [],
      loading: false,
      dialogNueva: false,
      creando: false,
      nueva: { tipo: "LISTA", proveedor: null, lista: null, archivo: null, nombre: "", sucursales: [] },
      proveedores: [],
      buscarProveedor: null,
      listas: [],
      sucursales: [],
      origenes: { LISTA: "Lista del proveedor", EXCEL: "Excel", MANUAL: "A mano", FACTURA: "Factura" },
      debounce: null,
    };
  },
  computed: {
    ...mapGetters("auth", ["puedeUsarAsistente"]),
    puedeCrear() {
      if (this.nueva.tipo === "LISTA") return !!this.nueva.lista;
      if (this.nueva.tipo === "EXCEL" || this.nueva.tipo === "FACTURA") return !!this.nueva.archivo;
      return true;
    },
  },
  watch: {
    buscarProveedor(texto) {
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => this.fetchProveedores(texto), 300);
    },
  },
  methods: {
    async fetch() {
      this.loading = true;
      try {
        const { data } = await this.axios.get("/productos/cargas");
        this.cargas = data.results || data;
      } finally {
        this.loading = false;
      }
    },
    async fetchProveedores(texto) {
      const params = { page: 1, page_size: 20 };
      if (texto) params.search = texto;
      const { data } = await this.axios.get("/proveedores/", { params });
      this.proveedores = data.results || data;
    },
    async fetchListas() {
      this.nueva.lista = null;
      this.listas = [];
      if (!this.nueva.proveedor) return;
      const { data } = await this.axios.get("/productos/cargas/listas", { params: { proveedor: this.nueva.proveedor } });
      this.listas = data;
      if (data.length) this.nueva.lista = data[0].id;
    },
    async abrirNueva() {
      this.nueva = { tipo: "LISTA", proveedor: null, lista: null, archivo: null, nombre: "", sucursales: [] };
      this.dialogNueva = true;
      this.fetchProveedores();
      if (!this.sucursales.length) {
        const { data } = await this.axios.get("/sucursales/sucursales");
        this.sucursales = data.results || data;
      }
    },
    async crear() {
      this.creando = true;
      try {
        if (this.nueva.tipo === "FACTURA") {
          const form = new FormData();
          form.append("archivo", this.nueva.archivo);
          this.nueva.sucursales.forEach((id) => form.append("sucursales", id));
          const { data: carga } = await this.axios.post("/productos/cargas/factura", form);
          this.dialogNueva = false;
          this.abrir(carga);
          return;
        }
        const cuerpo = {
          origen: this.nueva.tipo,
          proveedor: this.nueva.proveedor,
          nombre: this.nueva.nombre || undefined,
          sucursales: this.nueva.sucursales.length ? this.nueva.sucursales : undefined,
        };
        if (this.nueva.tipo === "LISTA") cuerpo.lista = this.nueva.lista;
        const { data: carga } = await this.axios.post("/productos/cargas", cuerpo);
        if (this.nueva.tipo === "EXCEL") {
          const form = new FormData();
          form.append("file", this.nueva.archivo);
          const { data } = await this.axios.post(`/productos/cargas/${carga.id}/excel`, form);
          if (data.errores && data.errores.length) {
            this.$updateBus.$emit("showSnackBar", {
              show: true,
              color: "warning",
              text: `${data.errores.length} filas del Excel no se pudieron leer (primera: fila ${data.errores[0].fila}, ${data.errores[0].error}).`,
              timeout: 8000,
            });
          }
        }
        this.dialogNueva = false;
        this.abrir(carga);
      } catch (e) {
        this.$updateBus.$emit("showSnackBar", { show: true, color: "error", text: getApiErrorMessage(e, "No se pudo crear la carga") });
      } finally {
        this.creando = false;
      }
    },
    abrir(carga) {
      this.$router.push({ name: "empresa.productos.cargaMesa", params: { id: carga.id } });
    },
    async descartar(carga) {
      await this.axios.delete(`/productos/cargas/${carga.id}`);
      this.fetch();
    },
    fecha(valor) {
      return valor ? new Date(valor).toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" }) : "";
    },
  },
  created() {
    this.fetch();
  },
};
</script>

<style scoped>
.carga {
  cursor: pointer;
  border-radius: 12px;
}
</style>
